/**
 * React Query Client Configuration
 *
 * Central configuration for TanStack Query (React Query).
 * Provides sensible defaults for caching, retry logic,
 * and stale-while-revalidate behavior.
 */

import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data is considered fresh for 30 seconds
      staleTime: 30 * 1000,
      // Keep unused data in cache for 5 minutes
      gcTime: 5 * 60 * 1000,
      // Retry failed requests up to 2 times with exponential backoff
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
      // Off, globally, rather than per-query.
      //
      // Every query that shows live dispatch data (cast status, cast
      // management, driver management, the admin dashboard) already
      // invalidates itself from the Realtime events the database publishes,
      // so a focus refetch re-fetches data that is being pushed anyway. It
      // also fires on alt-tab, which is a non-interaction — and "the screen
      // changes while I am not touching it" is the complaint this whole change
      // exists to fix. Disabling it only for the heavy dashboard queries would
      // be backwards: those are precisely the ones whose reflow is disruptive.
      //
      // Freshness on (re)entering a screen is still covered by
      // refetchOnMount + staleTime, and pull-to-refresh remains explicit.
      refetchOnWindowFocus: false,
      // Don't refetch on component mount if data is still fresh
      refetchOnMount: true,
    },
    mutations: {
      // Don't retry mutations by default
      retry: 0,
    },
  },
});
